import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import propTypes from "prop-types";
import classnames from "classnames";

import Button from "./Button";
import "./ButtonDropNav.scss";
import { createDataAttributes } from "../../utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";

const ButtonDropNavMenu = ({ links, setShow, mainRef, mainOnClick }) => {
  const [pos, setPos] = useState({});

  const menuRef = useRef(null);

  const handleClick = (link, event) => {
    event.stopPropagation();
    event.preventDefault();
    setShow(false);

    if (mainOnClick) {
      mainOnClick(link);
      return false;
    }

    if (link.onClick) {
      link.onClick(link);
    }
    if (link.to) {
      if (link.newWindow) {
        window.open(link.to, "_blank");
      } else {
        window.location = link.to;
      }
    }
  };

  // handle clicking outside of the menu to close the menu
  useEffect(() => {
    const handleClickOutside = event => {
      if (mainRef.current && !mainRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    const escKey = evt => {
      evt = evt || window.event;
      if (evt.keyCode === 27) {
        setShow(false);
      }
    };

    document.addEventListener("keydown", escKey, true);
    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      document.removeEventListener("keydown", escKey, true);
    };
  }, [mainRef, setShow]);

  // handle the position of the menu in case it doesn't fit in the viewport when the component renders
  useLayoutEffect(() => {
    const mainRect = mainRef?.current
      ? mainRef.current.getBoundingClientRect()
      : {};

    const menuRect = menuRef?.current
      ? menuRef.current.getBoundingClientRect()
      : {};

    const spaceBelow = window.innerHeight - (mainRect?.bottom || 0);
    const menuHeight = menuRect?.height;

    const spaceRight = window.innerWidth - (mainRect?.right || 0);
    const menuWidth = menuRect?.width;

    const showAbove = menuHeight && spaceBelow < menuHeight;
    const showLeft = menuWidth && spaceRight < menuWidth;

    if (mainRect && menuRect)
      setPos({
        top: showAbove
          ? "-" + (menuRect.height + 10) + "px"
          : mainRect.height + 5 + "px",
        right: showLeft ? 0 : "auto"
      });
  }, [mainRef]);

  const classes = classnames("uikit__button-dropnav__menu");

  return (
    <div ref={menuRef} className={classes} style={pos}>
      {links.map(l => (
        <button
          key={l.text}
          className="uikit__button-dropnav__menu__item"
          onClick={e => handleClick(l, e)}
        >
          {l.text}
        </button>
      ))}
    </div>
  );
};

const ButtonDropNav = ({
  buttonText,
  children,
  className,
  dataAttrib,
  disabled,
  id,
  links,
  onClick,
  size,
  tabIndex,
  variant,
  wrapperClassName,
  icon,
  ...rest
}) => {
  const mainRef = useRef(null);

  const [show, setShow] = useState(false);

  const wrapperClasses = classnames("uikit__button-dropnav", wrapperClassName);
  const buttonClasses = classnames(className, {
    uikit__btn__small: size === "sm",
    uikit__btn__large: size === "lg",
    "uikit__btn__with-icon": icon
  });

  const handleClick = event => {
    event.stopPropagation();
    event.preventDefault();
    setShow(p => !p);
  };

  return (
    <span
      ref={mainRef}
      className={wrapperClasses}
      {...createDataAttributes(dataAttrib)}
    >
      <Button
        tabIndex={tabIndex}
        id={id}
        onClick={handleClick}
        icon={icon}
        iconPos={icon ? "left" : null}
        variant={variant}
        disabled={disabled}
        className={buttonClasses}
        {...rest}
      >
        {buttonText || children}
        <FontAwesomeIcon
          className="uikit__button-dropnav__icon-caret"
          icon={faCaretDown}
        />
      </Button>
      {show && (
        <ButtonDropNavMenu
          links={links}
          mainRef={mainRef}
          setShow={setShow}
          mainOnClick={onClick}
        />
      )}
    </span>
  );
};

ButtonDropNav.propTypes = {
  id: propTypes.oneOfType([propTypes.string, propTypes.number]).isRequired,
  children: propTypes.oneOfType([
    propTypes.element,
    propTypes.string,
    propTypes.node
  ]),
  tabIndex: propTypes.number,
  buttonText: propTypes.string,
  disabled: propTypes.bool,
  links: propTypes.array.isRequired,
  className: propTypes.string,
  onClick: propTypes.func,
  variant: propTypes.oneOf([
    "primary",
    "secondary",
    "tertiary",
    "plain",
    "link"
  ]),
  size: propTypes.oneOf(["sm", "md", "lg"]),
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object),
  wrapperClassName: propTypes.string,
  icon: propTypes.oneOfType([propTypes.string, propTypes.element])
};

ButtonDropNav.defaultProps = {
  disabled: false,
  variant: "plain",
  size: "md",
  tabIndex: 0
};

export default ButtonDropNav;
